import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { FileText, Loader2 } from "lucide-react";
import { ChatPanel } from "@/components/ui/ChatPanel";
import { useDocumentChat } from "./hooks/use-document-chat";
import { ChatMessageBubble } from "./chat/ChatMessageBubble";
import { ChatQuickActions } from "./chat/ChatQuickActions";
import { ChatThreadHeader } from "./chat/ChatThreadHeader";
import { useTranslation } from "@audit-app/utils";
import type { DocumentView } from "./types";
import type { Id } from "@/packages/types";

interface DocumentDetailChatProps {
  document: DocumentView;
}

export function DocumentDetailChat({ document }: DocumentDetailChatProps) {
  const { t } = useTranslation(["documents", "chat"]);
  const docId = document.id as Id<"documents">;
  const isProcessing =
    document.aiSummary?.status === "generating" ||
    document.status === "under-review";

  const {
    messages,
    sendMessage,
    isStreaming,
    isSending,
    isLoading,
    threads,
    threadId,
    startNewThread,
    selectThread,
    deleteThread,
    renameThread,
  } = useDocumentChat(docId);

  const bottomRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom on every render (matches ChatPanelMessages pattern)
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  });

  const hasMessages = messages.length > 0;

  const handleQuickAction = (text: string) => {
    sendMessage(text);
  };

  return (
    <ChatPanel inline>
      {/* Thread selector header — shown when threads exist */}
      {threads.length > 0 ? (
        <ChatThreadHeader
          threads={threads}
          activeThreadId={threadId}
          onNewThread={startNewThread}
          onSelectThread={selectThread}
          onDeleteThread={deleteThread}
          onRenameThread={renameThread}
        />
      ) : null}

      {/* Messages area */}
      {isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <motion.div
            className="flex items-center gap-2 text-muted"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="h-4 w-4 border-2 border-muted/30 border-t-muted rounded-full animate-spin" />
            <span className="text-sm">
              {t("documents:documentDetailChat.loadingConversation")}
            </span>
          </motion.div>
        </div>
      ) : !hasMessages ? (
        <div className="flex-1 flex flex-col items-center justify-center overflow-y-auto">
          {isProcessing ? (
            <div className="flex flex-col items-center justify-center text-center px-6 py-12 gap-4">
              <div className="h-12 w-12 rounded-full bg-accent/10 flex items-center justify-center">
                <Loader2 className="h-6 w-6 text-accent animate-spin" />
              </div>
              <div className="space-y-1.5">
                <p className="text-sm font-medium text-primary">
                  {t("documents:chatEmptyState.analyzingDocument")}
                </p>
                <p className="text-xs text-muted max-w-[240px]">
                  {t("documents:chatEmptyState.processingDescription")}
                </p>
              </div>
            </div>
          ) : (
            <ChatQuickActions onAction={handleQuickAction} />
          )}
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {messages.map((message, i) => (
            <ChatMessageBubble key={message.id} message={message} index={i} />
          ))}
          {isStreaming && messages[messages.length - 1]?.role === "user" && (
            <div className="flex items-start">
              <div className="bg-card text-muted rounded-2xl rounded-bl-sm px-4 py-2.5">
                <div className="flex items-center gap-2 text-sm">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  <span>{t("chat:thinking")}</span>
                </div>
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>
      )}

      {/* Input bar */}
      <ChatPanel.Input
        placeholder={
          isProcessing
            ? t("documents:documentDetailChat.documentProcessing")
            : isSending
              ? t("documents:documentDetailChat.sending")
              : t("documents:documentDetailChat.askAboutDocument")
        }
        onSend={sendMessage}
        context={{
          label: document.title,
          icon: FileText,
        }}
      />
    </ChatPanel>
  );
}
